import { useCallback, useState } from 'react';
import { DirectUpload } from '@rails/activestorage';
import { useAtomValue } from 'jotai';
import { rootResourceAtom } from 'atoms/root';

/**
 * 100 MB, deliberately not lib/atoms/file-atom's 500 MB.
 *
 * Sized to the thing people actually attach: a 30-second Retina screen recording — the
 * default on a MacBook — runs 30-55 MB, so the previous 25 MB rejected the common case
 * rather than an extreme one. 100 MB covers that and phone recordings without inviting 4K
 * camera footage nobody can watch in a ticket.
 *
 * The tracker's own per-file cap (Jira::UploadAttachments::JIRA_MAX_BYTES) is the same
 * number, so nothing accepted here is too large to copy onto the ticket. If the two ever
 * diverge again, the oversized file is still stored and shown on the report and the ticket
 * says why it is not attached — but the form stops warning about a case that cannot arise.
 */
export const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024;

/** Mirrors BugsFeature's allowed types; the server re-checks. */
export const ACCEPTED_TYPES = 'image/*,video/*,application/pdf';

/**
 * The same list, as prefixes, for checking a file we already hold.
 *
 * ACCEPTED_TYPES is the `accept` hint on the picker, which filters the browse dialog and
 * nothing else — drag-and-drop and paste both hand files straight to #addFiles. So an
 * unsupported file uploaded, rendered as `complete` beside the valid ones, and its signed id
 * went into the submit; the server then refused the whole report with "must be an image or a
 * video" and named no file, leaving the reporter to remove them one at a time.
 */
const ALLOWED_TYPE_PREFIXES = ['image/', 'video/'];

/**
 * Mirrors BugsFeature::ALLOWED_ATTACHMENT_MIME_TYPES.
 *
 * Separate from the prefixes because a family and an exact type are different rules: allowing
 * `application/` would take every executable and archive with it, where a PDF is one type.
 */
const ALLOWED_EXACT_TYPES = ['application/pdf'];

/**
 * Whether the browser recognised this file as something the report accepts.
 *
 * An empty type means the browser could not tell — a format it has no mapping for, which is
 * by definition not something it could render. Refused here rather than sent for
 * the server to refuse less helpfully.
 *
 * @param {File} file The file the picker, a drop or a paste handed over.
 * @return {boolean} True when the report will accept it.
 */
export const isSupportedAttachment = (file: File): boolean =>
  ALLOWED_TYPE_PREFIXES.some((prefix) => file.type.startsWith(prefix)) ||
  ALLOWED_EXACT_TYPES.includes(file.type);

export interface PendingAttachment {
  localId: string;
  name: string;
  byteSize: number;
  status: 'uploading' | 'complete' | 'error';
  /** Active Storage signed id, present once the direct upload finishes. */
  signedId?: string;
  error?: string;
}

const formatMb = (bytes: number) => Math.round(bytes / (1024 * 1024));

export function useAttachmentUpload() {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const root = useAtomValue(rootResourceAtom);
  const directUploadUrl =
    root && 'directUploads' in root.resources
      ? root.resources.directUploads.href
      : null;

  const update = useCallback(
    (localId: string, patch: Partial<PendingAttachment>) => {
      setAttachments((prev) =>
        prev.map((a) => (a.localId === localId ? { ...a, ...patch } : a))
      );
    },
    []
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (!directUploadUrl) return;

      files.forEach((file) => {
        const localId = crypto.randomUUID();

        // Checked alongside the size, and before the upload, for the same reason: the
        // server refuses this file either way, and refusing it here names which one.
        if (!isSupportedAttachment(file)) {
          setAttachments((prev) => [
            ...prev,
            {
              localId,
              name: file.name,
              byteSize: file.size,
              status: 'error',
              error: 'Only images, video and PDFs can be attached',
            },
          ]);

          return;
        }

        // Rejected before upload rather than after, so the user is not made to
        // wait for a transfer that was never going to be accepted.
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setAttachments((prev) => [
            ...prev,
            {
              localId,
              name: file.name,
              byteSize: file.size,
              status: 'error',
              error: `Over the ${formatMb(MAX_ATTACHMENT_BYTES)} MB limit`,
            },
          ]);
          return;
        }

        setAttachments((prev) => [
          ...prev,
          {
            localId,
            name: file.name,
            byteSize: file.size,
            status: 'uploading',
          },
        ]);

        // Uploads straight to storage, so the report submit stays a small JSON
        // request carrying signed ids rather than a multipart body.
        new DirectUpload(file, directUploadUrl).create((error, blob) => {
          if (error) {
            update(localId, { status: 'error', error: 'Upload failed' });
          } else {
            update(localId, { status: 'complete', signedId: blob.signed_id });
          }
        });
      });
    },
    [directUploadUrl, update]
  );

  const remove = useCallback((localId: string) => {
    setAttachments((prev) => prev.filter((a) => a.localId !== localId));
  }, []);

  const reset = useCallback(() => setAttachments([]), []);

  return {
    attachments,
    addFiles,
    remove,
    reset,
    /** Signed ids ready to submit; excludes in-flight and failed uploads. */
    signedIds: attachments
      .filter((a) => a.status === 'complete' && a.signedId)
      .map((a) => a.signedId as string),
    isUploading: attachments.some((a) => a.status === 'uploading'),
    canUpload: Boolean(directUploadUrl),
  };
}
